import Axios from "../_helpers/axiosHelper.ts";

// Types for the billing API
export interface BillingAddress {
  address: string;
  postal_code: string;
  city: string;
  country_alpha2: string;
}

export interface PlanItem {
  number: string;
  label: string;
  enabled: boolean;
  vat_rate: string;
  country_alpha2: string;
  description: string;
  v2_id: number;
}

export interface Customer {
  id: number;
  customer_type: "company" | "individual";
  name: string;
  billing_iban: string | null;
  payment_conditions: string;
  recipient: string;
  created_at: string;
  updated_at: string;
  phone: string;
  reference: string;
  billing_language: string;
  vat_number: string;
  reg_no: string;
  emails: string[];
  billing_address: BillingAddress;
  delivery_address: BillingAddress;
  external_reference: string;
  ledger_account: {
    id: number;
  };
  notes: string | null;
  mandates: {
    url: string;
  };
  // Additional fields for individual customers
  last_name?: string;
  first_name?: string;
}

export interface LineItem {
  id: number;
  label: string;
  unit: string;
  quantity: string;
  amount: string;
  currency_amount: string;
  description: string;
  vat_rate: string;
  currency_price_before_tax: string;
  currency_tax: string;
  raw_currency_unit_price: string;
  product_id: string;
  product_v2_id: number;
  v2_id: number;
}

export interface Quote {
  amount: string;
  currency_amount_before_tax: string;
  currency_amount: string;
  currency_tax: string;
  currency: string;
  date: string;
  deadline: string;
  discount_type: string;
  discount: string;
  label: string;
  language: string;
  pdf_invoice_free_text: string;
  pdf_invoice_subject: string;
  updated_at: string;
  id: string;
  status: string;
  exchange_rate: number;
  file_url: string;
  estimate_number: string;
  estimate_type: string;
  source: string;
  customer: Customer & {
    source_id?: string;
    v2_id?: number;
  };
  line_items: LineItem[];
  related_invoices: any[];
  v2_id: number;
}

export interface Product {
  id: number;
  price_before_tax: string;
  vat_rate: string;
  price: string;
  unit: string;
  currency: string;
  reference: string;
  description: string;
  label: string;
  external_reference: string;
  ledger_account: any;
}

export interface CustomersResponse {
  items: Customer[];
  has_more: boolean;
  next_cursor?: string;
}

export interface QuotesResponse {
  total_pages: number;
  current_page: number;
  estimates: Quote[];
}

export interface ProductsResponse {
  items: Product[];
  has_more: boolean;
  next_cursor?: string;
}

export interface FilterCriteria {
  field: string;
  operator: string;
  value: string;
}

export interface ListCustomersParams {
  name?: string;
  page?: number;
  cursor?: string;
  limit?: number;
}

export interface ListQuotesParams {
  filter?: FilterCriteria[];
  page?: number;
  limit?: number;
}

export interface ListProductsParams {
  limit?: number;
  cursor?: string;
  version?: string;
}

export const listCustomers = (params: ListCustomersParams = {}): Promise<CustomersResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.name) {
    queryParams.append('name', params.name);
  }
  
  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  } else {
    queryParams.append('page', '1');
  }
  
  if (params.cursor) {
    queryParams.append('cursor', params.cursor);
  }
  
  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  const url = `/invoice/customers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return Axios.get(url);
};

export const listQuotes = (params: ListQuotesParams = {}): Promise<QuotesResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.filter && params.filter.length > 0) {
    queryParams.append('filter', JSON.stringify(params.filter));
  }
  
  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }
  
  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  const url = `/invoice/quotes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return Axios.get(url);
};

export const listProducts = (params: ListProductsParams = {}): Promise<ProductsResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  } else {
    queryParams.append('limit', '50');
  }
  
  if (params.cursor) {
    queryParams.append('cursor', params.cursor);
  }
  
  if (params.version !== undefined) {
    queryParams.append('version', params.version);
  } else {
    queryParams.append('version', 'v2');
  }

  const url = `/invoice/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return Axios.get(url);
};

export const listCompanyCustomers = (page?: number): Promise<CustomersResponse> => {
  const params: ListCustomersParams = {};
  
  if (page !== undefined) {
    params.page = page;
  } else {
    params.page = 1;
  }
  
  return listCustomers(params);
};

export const listAcceptedQuotes = (page?: number): Promise<QuotesResponse> => {
  const params: ListQuotesParams = {
    filter: [{ field: "status", operator: "scope", value: "estimate_accepted_status" }]
  };
  
  if (page !== undefined) {
    params.page = page;
  }
  
  return listQuotes(params);
};

export const listAllCustomers = (): Promise<CustomersResponse> => {
  return listCustomers();
};

export const listAllQuotes = (): Promise<QuotesResponse> => {
  return listQuotes();
};

export const listAllProducts = (): Promise<ProductsResponse> => {
  return listProducts();
};

export const searchCustomers = (name: string, page?: number): Promise<CustomersResponse> => {
  const params: ListCustomersParams = { 
    name,
    page: page || 1
  };
  
  return listCustomers(params);
};

export const searchCustomersWithDebounce = async (query: string): Promise<Customer[]> => {
  if (!query || query.trim().length < 2) {
    return [];
  }
  
  try {
    const response = await searchCustomers(query.trim(), 1);
    return response.items;
  } catch (error) {
    console.error('Error searching customers:', error);
    return [];
  }
};

export const generateCustomerInvoice = (
  serviceReportId: string
): Promise<any> => {
  return Axios.post(`/invoice/generate-customer-invoice/${serviceReportId}`);
};

export const getCustomerInvoiceByServiceReportId = (
  serviceReportId: string
): Promise<any> => {
  return Axios.get(`/invoice/service-report/${serviceReportId}`);
};

export interface AssociateQuoteToServiceRequest {
  serviceId: string;
  customerData: {
    source_id?: string;
    name?: string;
    customer_type?: "company" | "individual";
    address?: string;
    postal_code?: string;
    city?: string;
    country_alpha2?: string;
    emails?: string[];
    phone?: string;
    reg_no?: string;
    vat_number?: string;
    recipient?: string;
    billing_iban?: string;
    payment_conditions?: string;
    reference?: string;
    notes?: string;
  };
  estimateId?: string; // For existing estimates
  line_items?: Array<{
    label: string;
    quantity: number;
    source_id: string;
  }>;
}

export interface AssociateQuoteToServiceResponse {
  message: string;
  data: {
    diagnosticCustomer: Customer;
    estimate?: Quote;
  };
}

export const associateQuoteToService = (
  data: AssociateQuoteToServiceRequest
): Promise<AssociateQuoteToServiceResponse> => {
  return Axios.post('/invoice/associate-quote-to-service', data);
};

export const searchProductsWithDebounce = async (query: string): Promise<Product[]> => {
  if (!query || query.trim().length < 1) {
    return [];
  }
  
  try {
    if (/^\d+$/.test(query.trim())) {
      const response = await listProducts({ limit: 1000 });
      return response.items.filter(product => product.id.toString() === query.trim());
    }
    
    const response = await listProducts({ limit: 100 });
    return response.items.filter(product =>
      product.label.toLowerCase().includes(query.toLowerCase()) ||
      product.description.toLowerCase().includes(query.toLowerCase()) ||
      product.reference.toLowerCase().includes(query.toLowerCase()) ||
      product.external_reference.toLowerCase().includes(query.toLowerCase())
    );
  } catch (error) {
    console.error('Error searching products:', error);
    return [];
  }
};

export const updateEstimateStatus = (
  quoteId: string,
  serviceId: string,
  status: "accepted" | "denied"
): Promise<any> => {
  return Axios.put(`/invoice/quotes/${quoteId}/${serviceId}/status`, { status });
};